import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ArrowLeft, Clock, Database, List, Radio, Webhook } from 'lucide-react';
import { Assert } from '@cotera/utilities';
import { ModelScopeFactory } from '@cotera/client/v0/actions/framework';
import { Divider, Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import { cn } from '@cotera/client/v0/lib/utils';
import { ResourceSelectionModel } from '../resources/selection/resource-selection.model';
import { PaletteCard } from '../workflow/components/workflow-node-palette';
import {
  TriggerPickerContent,
  type TriggerSelection,
} from '../workflow/components/trigger-picker-content';
import {
  WebhookProviderConfigSection,
  WebhookProviderPicker,
  createDefaultWebhookProviderConfigDraft,
  findWebhookProviderCatalogEntry,
  useWebhookProviderCatalog,
  useWebhookProviderConfigDraft,
  type WebhookProviderConfigDraft,
} from '@cotera/client/app/v1/webhook-providers';
import { useAction } from '@cotera/client/v0/actions/framework';
import { SchedulePicker } from './schedule-picker';
import { humanReadableSchedule } from './schedule-utils';
import { webhookTriggerDescription } from './webhook-trigger-description';
import { datasetTriggerDescription } from './dataset-trigger-description';
import { useEmitEventNameSuggestions } from '../workflow/hooks/use-emit-event-name-suggestions';
import {
  useDatasetOptions,
  useDatasetShape,
  useWorkflowTriggerOptions,
} from './hooks';
import type { WorkflowTriggerOption } from './hooks';
import { DatasetPickerContent } from './dataset-picker-content';
import {
  DatasetTriggerConfig,
  createDefaultDatasetTriggerDraft,
  datasetTriggerDraftIsComplete,
  type DatasetTriggerDraft,
} from './dataset-trigger-config';
import {
  CreateStandaloneTriggerAction,
  type TriggerFireKind,
} from './create-standalone-trigger.action';

/**
 * The attach flow's result. The wizard runs outside the workflow scope, so
 * rather than mutate, it hands this back for the editor to bind onto the open
 * workflow's draft graph.
 */
export type AttachTriggerResult =
  | { type: 'existing'; trigger: WorkflowTriggerOption }
  | { type: 'new-cron'; schedule: string; name: string }
  | { type: 'new-webhook'; draft: WebhookProviderConfigDraft }
  | { type: 'new-event'; name: string; eventName: string }
  | { type: 'new-dataset'; name: string; draft: DatasetTriggerDraft };

/**
 * - `standalone` (triggers list "+"): builds a new workflow whose graph holds
 *   just the trigger node — nothing wired downstream yet.
 * - `attach` (workflow editor): attaches a trigger to the open workflow — adds a
 *   "reuse existing" option, returns via `onAttach`.
 */
export type NewTriggerWizardProps =
  | {
      mode?: 'standalone';
      folderId?: string;
      onCreated: (result: { workflowId: string }) => void;
      onCancel: () => void;
    }
  | {
      mode: 'attach';
      onAttach: (result: AttachTriggerResult) => Promise<boolean>;
      onCancel: () => void;
    };

// Standalone starts at `fire` (how it fires); attach starts at `pick` (the
// chooser). Webhook fires insert `webhook-provider` before `configure`.
type WizardStep =
  | 'pick'
  | 'existing'
  | 'fire'
  | 'webhook-provider'
  | 'dataset-select'
  | 'configure';

const STEP_TRANSITION = { duration: 0.2, ease: [0.4, 0, 0.2, 1] } as const;

const STEP_VARIANTS = {
  enter: (dir: number) => ({ opacity: 0, x: dir * 16 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir * -16 }),
};

const DATASET_CARD = {
  title: 'When a dataset changes',
  description: 'Run for each row added, changed or removed.',
} as const;

/**
 * The configure step's heading, per way of firing. Each one leads with the most
 * specific thing known at that point — the app or the dataset already chosen —
 * and says in one sentence what the trigger will do once created.
 */
const CONFIGURE_HEADER: Record<
  TriggerFireKind | 'unknown',
  (context: {
    providerName: string | null;
    /** The provider's own summary of what it covers, from the catalog. */
    providerDescription: string | null;
    datasetName: string | null;
  }) => {
    title: string;
    subtitle: string;
  }
> = {
  cron: () => ({
    title: 'On a schedule',
    subtitle: 'The workflow runs every time the schedule comes round.',
  }),
  webhook: ({ providerName, providerDescription }) => ({
    title: providerName ?? 'From an app',
    // The catalog's own description ("Linear issue, comment, and project
    // webhooks") says what the provider covers. A sentence of ours restating
    // the provider name does not, and the name is already the title.
    subtitle:
      providerDescription ??
      'The workflow runs whenever the app sends an event.',
  }),
  event: () => ({
    title: 'On a workflow event',
    subtitle: 'The workflow runs when another workflow emits this event.',
  }),
  dataset: ({ datasetName }) => ({
    title: datasetName ?? DATASET_CARD.title,
    subtitle: 'The workflow runs for each row that matches.',
  }),
  // Attaching to an existing trigger: the trigger is already made, so there is
  // no firing choice of ours to describe.
  unknown: () => ({ title: 'Configure', subtitle: 'Set the details' }),
};

const DEFAULT_CRON = '0 9 * * *';

function normalizeCronForMatch(cron: string): string {
  return cron.trim().replace(/\s+/g, ' ');
}

function findMatchingCronTrigger(
  existingTriggers: readonly WorkflowTriggerOption[],
  schedule: string,
  name: string
): WorkflowTriggerOption | undefined {
  const normalizedSchedule = normalizeCronForMatch(schedule);
  const normalizedName = name.trim();
  const scheduleMatches = existingTriggers.filter(
    (trigger) =>
      trigger.kind === 'cron' &&
      trigger.schedule !== null &&
      normalizeCronForMatch(trigger.schedule) === normalizedSchedule
  );
  return (
    scheduleMatches.find((trigger) => trigger.name.trim() === normalizedName) ??
    scheduleMatches[0]
  );
}

export const NewTriggerWizard: React.FC<NewTriggerWizardProps> = (props) => {
  const createModels = useCallback(() => [new ResourceSelectionModel()], []);
  return (
    <ModelScopeFactory createModels={createModels} deps={[]}>
      <NewTriggerWizardBody {...props} />
    </ModelScopeFactory>
  );
};

const NewTriggerWizardBody: React.FC<NewTriggerWizardProps> = (props) => {
  const isAttach = props.mode === 'attach';
  const { onCancel } = props;

  const existingTriggers = useWorkflowTriggerOptions();
  const datasets = useDatasetOptions();
  const eventNameSuggestions = useEmitEventNameSuggestions();
  const { providers, isLoading: catalogLoading } = useWebhookProviderCatalog();
  const { execute: createTrigger } = useAction(CreateStandaloneTriggerAction);
  const {
    draft: webhookDraft,
    patchDraft: patchWebhookDraft,
    resetDraft: resetWebhookDraft,
  } = useWebhookProviderConfigDraft();

  const [step, setStep] = useState<WizardStep>(isAttach ? 'pick' : 'fire');
  const [direction, setDirection] = useState(1);
  const [attaching, setAttaching] = useState(false);

  const [fires, setFires] = useState<TriggerFireKind | null>(null);
  // Attach mode only: the existing trigger / new-trigger kind the user picked.
  const [attachSelection, setAttachSelection] =
    useState<TriggerSelection | null>(null);

  const [schedule, setSchedule] = useState(DEFAULT_CRON);
  const [eventName, setEventName] = useState('');
  const [webhookProviderKey, setWebhookProviderKey] = useState<string | null>(
    null
  );
  const [webhookCredentialConnected, setWebhookCredentialConnected] =
    useState(false);
  const [datasetDraft, setDatasetDraft] = useState<DatasetTriggerDraft | null>(
    null
  );
  const [creating, setCreating] = useState(false);

  const navigate = (next: WizardStep, dir: number): void => {
    setDirection(dir);
    setStep(next);
  };

  // The scroll body is one element the steps animate inside, so its position
  // carries across steps — reset it to the top on each step change.
  const scrollRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [step]);

  const webhookCatalog = useMemo(
    () =>
      webhookProviderKey === null
        ? undefined
        : findWebhookProviderCatalogEntry(providers, webhookProviderKey),
    [providers, webhookProviderKey]
  );

  // Whether this provider needs an org-supplied credential (e.g. WorkOS API
  // key) before it can be installed. The connected state is reported up by the
  // credential section (which reads the shared, event-driven provider-account
  // cache) so the create button can gate on it.
  const webhookCredentialRequired =
    webhookCatalog?.credentials?.kind === 'provider_account';

  const webhookReady =
    webhookCatalog !== undefined &&
    (webhookCatalog.events.length === 0 ||
      webhookDraft.eventKey !== null ||
      webhookCatalog.setup.kind === 'none') &&
    (!webhookCredentialRequired || webhookCredentialConnected);

  const datasetShape = useDatasetShape(datasetDraft?.datasetId ?? null);

  /*
   * Triggers aren't named by hand any more — the list identifies a trigger by
   * what makes it fire, so a name is only a label for the surfaces that still
   * show one (the Library, search, and the slug an email address is minted
   * from). Deriving it from the same facts the row displays keeps those in step
   * without asking for something the user has no basis to answer.
   */
  const selectedDatasetName =
    datasets.find((dataset) => dataset.id === datasetDraft?.datasetId)?.name ??
    null;

  const derivedName = ((): string => {
    if (fires === 'cron') {
      return schedule.trim() !== ''
        ? humanReadableSchedule(schedule)
        : 'Schedule';
    }
    if (fires === 'event') {
      return eventName.trim() !== '' ? eventName.trim() : 'Event';
    }
    if (fires === 'dataset') {
      return datasetTriggerDescription({
        datasetName: selectedDatasetName,
        criterion: datasetDraft?.criterion ?? null,
        runMode: datasetDraft?.runMode ?? null,
      });
    }
    // The same rule the Automations row uses, so the name stored here is the
    // name the trigger will be listed under. No address yet — storage mints it
    // during the write — so an email trigger lands as "Email" and the row
    // switches to the address once it exists.
    return webhookTriggerDescription(
      {
        providerKey: webhookProviderKey,
        eventKey: webhookDraft.eventKey,
        inboundEmailAddress: null,
      },
      providers
    );
  })();

  const startNewCron = (): void => {
    setFires('cron');
    setSchedule(DEFAULT_CRON);
    navigate('configure', 1);
  };

  const startNewWebhook = (): void => {
    setFires('webhook');
    setWebhookProviderKey(null);
    setWebhookCredentialConnected(false);
    resetWebhookDraft(createDefaultWebhookProviderConfigDraft());
    navigate('webhook-provider', 1);
  };

  const startNewEvent = (): void => {
    setFires('event');
    setEventName('');
    navigate('configure', 1);
  };

  const startNewDataset = (): void => {
    setFires('dataset');
    setDatasetDraft(null);
    navigate('dataset-select', 1);
  };

  /** Whether the config step for `fires` has everything it needs to save. */
  const firesConfigReady = ((): boolean => {
    switch (fires) {
      case null:
        return false;
      case 'cron':
        return schedule.trim() !== '';
      case 'event':
        return eventName.trim() !== '';
      case 'dataset':
        return (
          datasetDraft !== null && datasetTriggerDraftIsComplete(datasetDraft)
        );
      case 'webhook':
        return webhookReady;
      default:
        return Assert.unreachable(fires);
    }
  })();

  const canCreate = firesConfigReady;

  const canAttach = ((): boolean => {
    if (attachSelection === null) {
      return false;
    }
    if (attachSelection.type === 'existing') {
      return true;
    }
    if (attachSelection.type === 'new-cron') {
      return schedule.trim() !== '';
    }
    if (attachSelection.type === 'new-event') {
      return derivedName !== '' && eventName.trim() !== '';
    }
    if (attachSelection.type === 'new-dataset') {
      return (
        datasetDraft !== null && datasetTriggerDraftIsComplete(datasetDraft)
      );
    }
    return webhookReady;
  })();

  const busy = isAttach ? attaching : creating;

  const handleCreate = async (): Promise<void> => {
    if (props.mode === 'attach' || !canCreate || fires === null) {
      return;
    }
    setCreating(true);
    try {
      const result = await createTrigger({
        fires,
        name: derivedName,
        folderId: props.folderId,
        schedule,
        eventName,
        webhook: webhookDraft,
      });
      if (result.isOk() && result.value.resourceId !== undefined) {
        props.onCreated({ workflowId: result.value.resourceId });
      }
    } finally {
      setCreating(false);
    }
  };

  const handleAttach = async (): Promise<void> => {
    if (props.mode !== 'attach' || attachSelection === null || !canAttach) {
      return;
    }
    let result: AttachTriggerResult | null = null;
    if (attachSelection.type === 'existing') {
      const trigger = existingTriggers.find(
        (option) => option.id === attachSelection.triggerId
      );
      if (trigger === undefined) {
        return;
      }
      result = { type: 'existing', trigger };
    } else if (attachSelection.type === 'new-cron') {
      const matchingTrigger = findMatchingCronTrigger(
        existingTriggers,
        schedule,
        derivedName
      );
      result =
        matchingTrigger !== undefined
          ? { type: 'existing', trigger: matchingTrigger }
          : { type: 'new-cron', schedule, name: derivedName };
    } else if (attachSelection.type === 'new-event') {
      result = {
        type: 'new-event',
        name: derivedName,
        eventName: eventName.trim(),
      };
    } else if (attachSelection.type === 'new-dataset') {
      if (datasetDraft === null) {
        return;
      }
      result = { type: 'new-dataset', name: derivedName, draft: datasetDraft };
    } else if (webhookCatalog !== undefined) {
      // The Name field is the canonical trigger name; fold it into the draft
      // (whose own `name` is unused in the wizard and defaults to "Webhook").
      result = {
        type: 'new-webhook',
        draft: { ...webhookDraft, name: derivedName },
      };
    }
    if (result === null) {
      return;
    }
    setAttaching(true);
    try {
      // Caller pops the modal on success; on failure it stays open to retry.
      await props.onAttach(result);
    } finally {
      setAttaching(false);
    }
  };

  const header = ((): { title: string; subtitle: string } => {
    switch (step) {
      case 'pick':
        return {
          title: 'Add trigger',
          subtitle: 'Reuse an existing trigger or create a new one',
        };
      case 'existing':
        return {
          title: 'Reuse a trigger',
          subtitle: 'Pick a trigger already set up in your workspace',
        };
      case 'fire':
        return {
          title: 'How should it fire?',
          subtitle: 'Pick what kicks it off',
        };
      case 'webhook-provider':
        return {
          title: 'Choose a provider',
          subtitle: 'Where the inbound events come from',
        };
      case 'dataset-select':
        return {
          title: 'Choose a dataset',
          subtitle: 'Which dataset to watch for changes',
        };
      case 'configure':
        /*
         * Named for the thing being configured rather than for the act of
         * configuring. "Configure / Fires from an app" spent both lines
         * restating the choice just made on the previous step; the provider or
         * dataset already picked is the specific fact worth showing, and the
         * subtitle is the one sentence stating what the trigger will do.
         */
        return CONFIGURE_HEADER[isAttach || fires === null ? 'unknown' : fires](
          {
            providerName: webhookCatalog?.displayName ?? null,
            providerDescription: webhookCatalog?.description ?? null,
            datasetName: selectedDatasetName,
          }
        );
      default:
        return Assert.unreachable(step);
    }
  })();

  /*
   * The name preview, or null when it would say nothing new.
   *
   * A provider with no event picked yet derives to exactly the configure
   * heading ("Linear"), so showing it repeats the title one line further down.
   * Once an event is chosen it becomes "Linear · Issue created" and starts
   * earning its place, which is when it reappears.
   */
  const listedAsPreview = ((): string | null => {
    if (isAttach || fires === null) {
      return null;
    }
    if (fires === 'webhook' && webhookProviderKey === 'email') {
      // An email trigger is listed by the address storage mints during the
      // write, so the derived name never reaches the list. Promising "Email"
      // here would be a small lie.
      return 'its email address, once created';
    }
    return derivedName === header.title ? null : derivedName;
  })();

  const goBack = (): void => {
    switch (step) {
      case 'existing':
        return navigate('pick', -1);
      case 'webhook-provider':
      case 'dataset-select':
        return navigate(isAttach ? 'pick' : 'fire', -1);
      case 'configure':
        if (fires === 'webhook') {
          return navigate('webhook-provider', -1);
        }
        if (fires === 'dataset') {
          return navigate('dataset-select', -1);
        }
        return navigate(isAttach ? 'pick' : 'fire', -1);
      case 'fire':
      case 'pick':
        return;
      default:
        return Assert.unreachable(step);
    }
  };

  const stepBody = ((): React.ReactNode => {
    switch (step) {
      case 'pick':
        return (
          <div className="flex flex-col gap-2">
            {existingTriggers.length > 0 ? (
              <PaletteCard
                icon={List}
                accent="neutral"
                title="Reuse an existing trigger"
                description="Attach a schedule or webhook you've already set up."
                onSelect={() => {
                  setAttachSelection(null);
                  navigate('existing', 1);
                }}
              />
            ) : null}
            <PaletteCard
              icon={Clock}
              accent="trigger"
              title="On a schedule"
              description="Run on a repeating cron schedule."
              onSelect={() => {
                setAttachSelection({ type: 'new-cron' });
                startNewCron();
              }}
            />
            <PaletteCard
              icon={Webhook}
              accent="webhook"
              title="From an app"
              description="Run whenever a connected app sends an event."
              onSelect={() => {
                setAttachSelection({ type: 'new-webhook' });
                startNewWebhook();
              }}
            />
            <PaletteCard
              icon={Radio}
              accent="event"
              title="On a workflow event"
              description="Run when another workflow emits a named event."
              onSelect={() => {
                setAttachSelection({ type: 'new-event' });
                startNewEvent();
              }}
            />
            <PaletteCard
              icon={Database}
              accent="dataset"
              title={DATASET_CARD.title}
              description={DATASET_CARD.description}
              onSelect={() => {
                setAttachSelection({ type: 'new-dataset' });
                startNewDataset();
              }}
            />
          </div>
        );
      case 'existing':
        return (
          <TriggerPickerContent
            existingTriggers={existingTriggers}
            selection={attachSelection}
            onSelectionChange={setAttachSelection}
            showCreateNew={false}
          />
        );
      case 'fire':
        return (
          <div className="flex flex-col gap-2">
            <PaletteCard
              icon={Clock}
              accent="trigger"
              title="On a schedule"
              description="Run on a repeating cron schedule."
              onSelect={startNewCron}
            />
            <PaletteCard
              icon={Webhook}
              accent="webhook"
              title="From an app"
              description="Run whenever a connected app sends an event."
              onSelect={startNewWebhook}
            />
            <PaletteCard
              icon={Radio}
              accent="event"
              title="On a workflow event"
              description="Run when another workflow emits a named event."
              onSelect={startNewEvent}
            />
            <PaletteCard
              icon={Database}
              accent="dataset"
              title={DATASET_CARD.title}
              description={DATASET_CARD.description}
              onSelect={startNewDataset}
            />
          </div>
        );
      case 'webhook-provider':
        return catalogLoading ? (
          <Text.Caption className="text-muted-foreground block">
            Loading webhook providers…
          </Text.Caption>
        ) : (
          <WebhookProviderPicker
            providers={providers}
            selectedKey={webhookProviderKey}
            onSelect={(providerKey) => {
              setWebhookProviderKey(providerKey);
              setWebhookCredentialConnected(false);
              resetWebhookDraft(
                createDefaultWebhookProviderConfigDraft(providerKey)
              );
              navigate('configure', 1);
            }}
          />
        );
      case 'dataset-select':
        return (
          <DatasetPickerContent
            datasets={datasets}
            selectedDatasetId={datasetDraft?.datasetId ?? null}
            onSelect={(datasetId) => {
              setDatasetDraft(createDefaultDatasetTriggerDraft(datasetId));
              navigate('configure', 1);
            }}
          />
        );
      case 'configure':
        return (
          <div className="flex flex-col gap-6">
            {fires === 'cron' ? (
              <SchedulePicker
                value={schedule}
                onChange={(next) => {
                  setSchedule(next);
                }}
              />
            ) : fires === 'event' ? (
              <Field label="Event name">
                <Input
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder="order.created"
                />
                {/*
                 * Buttons rather than the `<datalist>` these used to fill: the
                 * names are the only clue what to type, and a datalist shows
                 * nothing until the user already guesses a prefix of one.
                 */}
                {eventNameSuggestions.length > 0 ? (
                  <div className="space-y-1.5 pt-1">
                    <Text.Caption className="text-muted-foreground block">
                      Emitted by workflows you already have:
                    </Text.Caption>
                    <div className="flex flex-wrap gap-1.5">
                      {eventNameSuggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => setEventName(suggestion)}
                          aria-pressed={eventName === suggestion}
                          className={cn(
                            'rounded-sm border px-2 py-0.5 font-mono text-xs transition-colors',
                            eventName === suggestion
                              ? 'border-slate-300 bg-secondary text-foreground'
                              : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                          )}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Text.Caption className="text-muted-foreground block pt-1">
                    Add an Emit event node to another workflow to send this
                    name.
                  </Text.Caption>
                )}
              </Field>
            ) : fires === 'dataset' && datasetDraft !== null ? (
              <DatasetTriggerConfig
                draft={datasetDraft}
                onChange={setDatasetDraft}
                shape={datasetShape}
              />
            ) : webhookCatalog !== undefined ? (
              <WebhookProviderConfigSection
                mode="create"
                catalog={webhookCatalog}
                draft={webhookDraft}
                onDraftChange={patchWebhookDraft}
                onSave={async () => {}}
                saving={busy}
                onCredentialStatusChange={setWebhookCredentialConnected}
              />
            ) : null}
            {/*
             * Not shown when attaching: that path reuses a trigger that is
             * already named, so there is nothing here to preview.
             */}
            {listedAsPreview !== null ? (
              <ListedAs name={listedAsPreview} />
            ) : null}
          </div>
        );
      default:
        return Assert.unreachable(step);
    }
  })();

  // Primary shows on `configure`, plus the `existing` drill-in (reuse needs no
  // further config). The `pick`/`run`/`fire` choosers just navigate onward.
  const showPrimary = step === 'configure' || (isAttach && step === 'existing');
  const canSubmit = isAttach ? canAttach : canCreate;
  const canGoBack = step !== 'fire' && step !== 'pick';

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        {canGoBack ? (
          <button
            type="button"
            onClick={goBack}
            className="-ml-1 mb-2 inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        ) : null}
        <Text.Title className="mb-1" type="section">
          {header.title}
        </Text.Title>
        <span className="mb-4 block text-sm text-muted-foreground">
          {header.subtitle}
        </span>
        <Divider className="mb-6" />
        <AnimatePresence mode="popLayout" custom={direction} initial={false}>
          <motion.div
            key={step}
            custom={direction}
            variants={STEP_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={STEP_TRANSITION}
          >
            {stepBody}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-6 flex shrink-0 justify-end gap-2 border-t pt-4">
        <Button variant="outline" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        {showPrimary ? (
          <Button
            onClick={() => void (isAttach ? handleAttach() : handleCreate())}
            disabled={!canSubmit || busy}
          >
            {isAttach
              ? busy
                ? 'Adding…'
                : 'Add trigger'
              : busy
              ? 'Creating...'
              : 'Create'}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

/**
 * What the trigger will be called once it exists.
 *
 * The wizard stopped asking for a name and derives one from the same facts the
 * Automations row shows. That is the right default, but it left the answer to be
 * discovered after creation — so state it here, live, while the fields that feed
 * it are still on screen.
 */
const ListedAs: React.FC<{ name: string }> = ({ name }) => (
  <div className="flex items-baseline gap-2 rounded-md border bg-muted/20 px-3 py-2">
    <span className="shrink-0 text-xs text-muted-foreground">Listed as</span>
    <span className="min-w-0 flex-1 truncate text-sm font-medium">{name}</span>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-slate-700">{label}</label>
    {children}
  </div>
);
